import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent, RouterExtensions } from '@luggagehero/core';
import {
  Address,
  CountryService,
  CreateManageableStorageLocation,
  IPLookupService,
  ManageableStorageLocation,
  PageTaggingService,
  PayoutService,
  Place,
  StorageManagerService,
  TimeInterval,
} from '@luggagehero/services';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { IPaymentRecord, IPlacesSearch, StorageLocationService } from '@luggagehero/shared/interfaces';
import { SharedStorageService } from '@luggagehero/shared/services/storage';
import { TranslateService } from '@ngx-translate/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';

import { Config } from '@luggagehero/shared/environment';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { SharedWindowService } from '@luggagehero/shared/services/window';
import { ImagesBaseComponent } from './images.base-component';
import { ManageExceptionalOpeningHoursBaseComponent } from './manage-exceptional-opening-hours.base-component';
import { ManageHolidayOpeningHoursBaseComponent } from './manage-holiday-opening-hours.base-component';
import { ManageOpeningHoursBaseComponent } from './manage-opening-hours.base-component';
import { SharedGoogleMapsService } from '@luggagehero/shared/services/google-maps';
import { SharedErrorService } from '@luggagehero/shared/services/error';
import { SharedAnalyticsService } from '@luggagehero/shared/services/analytics';
import { SharedUserService } from '@luggagehero/shared/services/users';
import { SharedPageTaggingService } from '@luggagehero/shared/services/page-tagging';

const DEFAULT_SHOP_DESCRIPTION_PART_1 = [
  'Luggage storage made easy! Let us take care of your luggage while you',
  'explore *|SHOP_CITY|*. We will keep your belongings safe for you, so',
  "you don't have to worry about them - or worse yet drag them around with",
  'you! Just remember to double check the opening hours and confirm that',
  'the shop is open when you plan to drop off and pick up your bags.',
].join(' ');
const DEFAULT_SHOP_DESCRIPTION_PART_2 = [
  'You can make a booking in advance to be sure there is enough space left',
  'when you arrive. Bookings are not binding and there are no fees for',
  "no-shows. We think that's how luggage storage should be - easy and risk",
  'free. Remember that by booking with LuggageHero and checking in online',
  'when you drop off your luggage, each of your bags will be insured by us',
  'in case of any loss or damage.',
].join(' ');

@Component({ template: '' })
export abstract class EditShopBaseComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChild('form') public form: FormGroup;
  @ViewChild('imagesModal') public imagesModal: ModalDirective;
  @ViewChild('lhImages') public imagesComponent: ImagesBaseComponent;
  @ViewChild('openingHoursForm') public openingHoursForm: ManageOpeningHoursBaseComponent;
  @ViewChild('holidayOpeningHoursForm')
  public holidayOpeningHoursForm: ManageHolidayOpeningHoursBaseComponent;
  @ViewChild('exceptionalOpeningHoursForm')
  public exceptionalOpeningHoursForm: ManageExceptionalOpeningHoursBaseComponent;
  @ViewChild('placesSearch') public placesSearch?: IPlacesSearch;

  @Output() public done = new EventEmitter();
  @Output() public cancel = new EventEmitter();

  public autocomplete: google.maps.places.Autocomplete;
  public countries: string[];
  public mapCenter: google.maps.LatLngLiteral;
  public mapStyles: any;
  public mapOptions: any;
  public imageUrl = '';
  public isInitialized = false;

  public readonly numberOfSections = 8;
  public currentSection = 0;
  public payoutMethods: IPaymentRecord[] = [];
  public isPayoutAccountFlowInitialized = false;
  public isExistingShop: boolean;

  private ipLookupService = inject(IPLookupService);

  private paramsSubscription: Subscription;

  private _isLoading = false;
  private _errorMessage: string;
  private _shop: ManageableStorageLocation;
  private _shopId: string;
  private _isModal = false;
  private _hasNoVatNumber = false;
  private previousVatNumber: string;
  private _isNotOnGoogle = false;
  private _selectedPlaceName = '';
  private requireHolidays = true;
  private holidayHeaderClickCount = 0;
  private _mapShops: ManageableStorageLocation[];

  private requestedSection: number;
  private currentShopImages: string[];
  private _imagesChanged = false;
  private _imagesAdded = false;

  private isImpersonator = false;
  private urlSnapshot: string;

  constructor(
    protected payoutService: PayoutService,
    protected windowService: SharedWindowService,
    protected storageService: SharedStorageService,
    protected router: RouterExtensions,
    protected route: ActivatedRoute,
    protected storageManager: StorageManagerService,
    protected googleMapsService: SharedGoogleMapsService,
    protected loginService: SharedUserService,
    protected error: SharedErrorService,
    protected tag: SharedPageTaggingService,
    protected countryService: CountryService,
    protected notify: SharedNotificationService,
    protected translate: TranslateService,
    protected cd: ChangeDetectorRef,
    protected analytics: SharedAnalyticsService,
  ) {
    super();

    this.imageUrl = `${Config.environment.IMAGES_BASE_URL}/images`;

    this.mapStyles = googleMapsService.mapStyles;
    this.mapOptions = googleMapsService.mapOptions;
  }

  get isEditPrivilegedFieldsAllowed(): boolean {
    return this.shop && (this.isNewShop || this.isImpersonator);
  }

  get isNewShop(): boolean {
    return !this.isExistingShop;
  }

  get user() {
    return this.loginService.user;
  }

  get progress(): number {
    return Math.round((this.currentSection / this.numberOfSections) * 100);
  }

  get confirmationNotification(): boolean {
    const value = this.shop && this.shop.notifications && this.shop.notifications.bookingConfirmation === true;
    return value;
  }
  set confirmationNotification(value: boolean) {
    this.shop.notifications.bookingConfirmation = value;
    this.cd.markForCheck();
  }

  get checkInNotification(): boolean {
    const value = this.shop && this.shop.notifications && this.shop.notifications.checkInConfirmation === true;
    return value;
  }
  set checkInNotification(value: boolean) {
    this.shop.notifications.checkInConfirmation = value;
    this.cd.markForCheck();
  }

  get checkOutNotification(): boolean {
    const value = this.shop && this.shop.notifications && this.shop.notifications.checkOutConfirmation === true;
    return value;
  }
  set checkOutNotification(value: boolean) {
    this.shop.notifications.checkOutConfirmation = value;
    this.cd.markForCheck();
  }

  get paymentFailedNotification(): boolean {
    const value = this.shop && this.shop.notifications && this.shop.notifications.onlinePaymentFailed === true;
    return value;
  }
  set paymentFailedNotification(value: boolean) {
    this.shop.notifications.onlinePaymentFailed = value;
    this.cd.markForCheck();
  }

  get paymentSuccessfulNotification(): boolean {
    const value = this.shop && this.shop.notifications && this.shop.notifications.onlinePaymentSuccessful === true;
    return value;
  }
  set paymentSuccessfulNotification(value: boolean) {
    this.shop.notifications.onlinePaymentSuccessful = value;
    this.cd.markForCheck();
  }

  get imagesChanged(): boolean {
    return this._imagesChanged;
  }
  set imagesChanged(value: boolean) {
    this._imagesChanged = value;
    this.cd.markForCheck();
  }

  get imagesAdded(): boolean {
    return this._imagesAdded;
  }
  set imagesAdded(value: boolean) {
    this._imagesAdded = value;
    this.cd.markForCheck();
  }

  get isCurrentSectionCompleted(): boolean {
    return this.isSectionCompleted(this.currentSection);
  }

  get isLastSection(): boolean {
    return this.currentSection === this.numberOfSections;
  }

  get canGoBack(): boolean {
    return this.currentSection !== 1;
  }

  get canGoForward(): boolean {
    if (this.isLastSection) {
      return this.canSaveShop;
    }
    return this.isCurrentSectionCompleted;
  }

  get isPristine(): boolean {
    if (this.form && !this.form.pristine) {
      return false;
    }
    if (this.openingHoursForm && !this.openingHoursForm.pristine) {
      return false;
    }
    if (this.holidayOpeningHoursForm && !this.holidayOpeningHoursForm.pristine) {
      return false;
    }
    if (this.exceptionalOpeningHoursForm && !this.exceptionalOpeningHoursForm.pristine) {
      return false;
    }
    if (this.imagesChanged) {
      return false;
    }
    return true;
  }

  get selectedPlaceName(): string {
    return this._selectedPlaceName;
  }
  set selectedPlaceName(value: string) {
    this._selectedPlaceName = value;
    this.cd.markForCheck();
  }

  get canSaveShop(): boolean {
    if (this.isImpersonator) {
      return true;
    }
    if (!this.form || !this.form.valid) {
      return false;
    }
    if (!this.openingHoursForm || !this.openingHoursForm.valid) {
      return false;
    }
    if (this.requireHolidays && this.holidayOpeningHoursForm && !this.holidayOpeningHoursForm.valid) {
      return false;
    }
    if (this.exceptionalOpeningHoursForm && !this.exceptionalOpeningHoursForm.valid) {
      return false;
    }
    if (this.isOnGoogle && !this.isPlaceSelected) {
      return false;
    }
    return true;
  }

  get hasNoVatNumber(): boolean {
    return this._hasNoVatNumber;
  }
  set hasNoVatNumber(value: boolean) {
    if (value) {
      // Clear input value to avoid saving invalid VAT numbers
      this.previousVatNumber = this.shop.vatNumber;
      this.shop.vatNumber = '';
    } else if (this.previousVatNumber) {
      // Restore previous input value if any
      this.shop.vatNumber = this.previousVatNumber;
      this.previousVatNumber = null;
    }
    this._hasNoVatNumber = value;
    this.cd.markForCheck();
  }

  get hasWifi(): boolean {
    return this.shop && this.shop.hasWifi;
  }
  set hasWifi(value: boolean) {
    this.shop.hasWifi = value;
    this.cd.markForCheck();
  }

  get isWifiCompleted(): boolean {
    if (!this.shop) {
      return false;
    }
    return this.shop.hasWifi === true || this.shop.hasWifi === false;
  }

  get isNotOnGoogle(): boolean {
    return this._isNotOnGoogle;
  }
  set isNotOnGoogle(value: boolean) {
    if (value) {
      this.shop.place = { id: null };
    } else if (this.shop.place && this.shop.place.id === null) {
      delete this.shop.place;
    }
    this._isNotOnGoogle = value;
    this.cd.markForCheck();
  }

  get isOnGoogle(): boolean {
    return !this.isNotOnGoogle;
  }

  get shop(): ManageableStorageLocation {
    return this._shop;
  }
  set shop(value: ManageableStorageLocation) {
    this._shop = value;
    this._shop.available = true;
    this.cd.markForCheck();
  }

  get shopId(): string {
    return this._shopId;
  }
  @Input() set shopId(value: string) {
    this._shopId = value;
    this.cd.markForCheck();
  }

  get isModal(): boolean {
    return this._isModal;
  }
  @Input() set isModal(value: boolean) {
    this._isModal = value;
  }

  get seeInsideUrl(): string {
    return this.shop.seeInsideUrl;
  }
  set seeInsideUrl(value: string) {
    if (!value || value === '') {
      return;
    }
    if (value.startsWith('<iframe')) {
      const begin = value.indexOf('src="') + 5;
      value = value.substring(begin);

      const end = value.indexOf('"');
      value = value.substring(0, end);
    }
    this.shop.seeInsideUrl = value;
    this.cd.markForCheck();
  }

  get externalReviewUrl(): string {
    return this.shop.externalReviewUrl;
  }
  set externalReviewUrl(value: string) {
    this.shop.externalReviewUrl = value || '';
    this.cd.markForCheck();
  }

  get isLoading(): boolean {
    return this._isLoading;
  }
  set isLoading(value: boolean) {
    this._isLoading = value;
    this.cd.markForCheck();
  }

  get errorMessage(): string {
    return this._errorMessage;
  }
  set errorMessage(value: string) {
    this._errorMessage = value;
    this.cd.markForCheck();
  }

  public get mapShops(): ManageableStorageLocation[] {
    return this._mapShops;
  }

  get mapInitialZoom(): number {
    return 16;
  }

  get isUploading(): boolean {
    if (!this.imagesComponent || !this.imagesComponent.uploader) {
      return false;
    }
    return this.imagesComponent.uploader.isUploading;
  }

  get wifiInfo(): any {
    if (!this.shop) {
      return null;
    }
    if (!this.shop.wifiInfo) {
      this.shop.wifiInfo = {};
    }
    return this.shop.wifiInfo;
  }

  get isPlaceSelected(): boolean {
    if (this.isNotOnGoogle) {
      return false;
    }
    return this.shop && this.shop.place ? true : false;
  }

  get isGuestStorageEnabled(): boolean {
    if (!this.shop?.services) {
      return false;
    }
    return this.shop.services.includes(StorageLocationService.GuestLuggageStorage);
  }

  async ngOnInit() {
    await this.windowService.ready();

    this.urlSnapshot = this.router.routerState.snapshot.url;
    this.isImpersonator = this.storageService.isImpersonator;

    if (this.shopId) {
      // Loaded as modal
      this.init();
    } else {
      this.paramsSubscription = this.route.params.subscribe((params) => {
        this.requestedSection = this.parseSection(params.section);
        this.requireHolidays = params.holidays !== 'optional';
        this.shopId = params.id ? String(params.id) : undefined;

        if (this.isInitialized) {
          this.onShopReady();
        } else {
          this.init();
        }
        if (!this.shopId) {
          this.analytics.track('storageLocationCreationInitiated', { user: this.user });
        }
      });
    }
    // get the countries for typeahead
    this.countries = this.countryService.getCountriesAsArray();

    this.tag.disableCrawling();
  }

  ngOnDestroy() {
    try {
      this.paramsSubscription.unsubscribe();
    } catch {
      // Ignore
    }
    this.tag.clearTags();
  }

  onShopReady() {
    this.isExistingShop = this.shop && this.shop._id ? true : false;

    //
    // HACK: Using timeout solves when there are updates queued that will cause UI elements
    // needed for the requested section to render.
    //
    setTimeout(() => this.loadSection(this.requestedSection || 1), 0);
  }

  async onPlaceChanged(value: Place) {
    if (this.isNewShop) {
      // Get full storage location object from the server based on the selected place
      const res = await this.storageManager.getStorageLocationByPlaceId(value.id);

      // Update the shop with the new data
      Object.assign(this.shop, res);
    }

    this.shop.place = value;
    this.updateShopAddress(value.address, true);
  }

  onImagesChanged(images: string[]) {
    if (!this.currentShopImages) {
      this.currentShopImages = this.shop.images;
    }
    this.imagesChanged = !this.areImagesEqual(this.currentShopImages, images);
    this.imagesAdded = images && images.length > 0;
    this.shop.images = images;
  }

  onHolidayHeaderClick() {
    if (++this.holidayHeaderClickCount >= 5) {
      this.requireHolidays = false;
      this.cd.markForCheck();
    }
  }

  onCancel() {
    if (this.isModal) {
      this.cancel.emit();
    } else {
      this.finish();
    }
  }

  clearSelectedPlace(e: Event) {
    e.stopPropagation();
    this.placesSearch?.clear();
    this.shop.place = null;
  }

  setShopDescription() {
    if (this.shop.description || !this.shop.name || !this.shop.address.city) {
      //
      // Don't add default description if a description already exists or if
      // the name or city is not available
      //
      return;
    }
    const descPart1 = DEFAULT_SHOP_DESCRIPTION_PART_1.replace('*|SHOP_NAME|*', this.shop.name)
      .replace('*|SHOP_CITY|*', this.shop.address.city)
      .replace('*|SHOP_CAPACITY|*', this.shop.capacity.normal.toString());
    const descPart2 = DEFAULT_SHOP_DESCRIPTION_PART_2;
    this.shop.description = `${descPart1}\n\n${descPart2}`;
  }

  //
  // Finds the latitude and lngitude from the address
  //
  geocodeAddress() {
    this.googleMapsService.geocode({ address: this.singleLineAddress() }).subscribe((res) => {
      const place = this.googleMapsService.parseGeocoderResult(res[0]);
      this.updateShopAddress(place.address, false);
    });
  }

  showImagesModal() {
    this.imagesModal.show();
  }

  async hideImagesModal() {
    this.shop = await this.storageManager.getStorageLocation(this.shop._id, true);
    this.imagesModal.hide();
  }

  async saveShop(doneEditing = true) {
    if (doneEditing && !this.canSaveShop) {
      this.goToFirstSectionWithMissingInfo();
      this.notifyUserOfMissingInfo();
      return;
    }

    this.isLoading = true;

    // Ensure valid url for shop
    if (this.shouldBeUrlPrefixed(this.shop.website)) {
      this.shop.website = `http://${this.shop.website}`;
    }

    // Normalize sms phone number format
    if (this.shop.sms && this.shop.sms.startsWith('00')) {
      this.shop.sms = `+${this.shop.sms.substring(2)}`;
    }

    if (this.shop.openingHours.holidays) {
      // Don't save holidays where opening hours have not been selected
      this.shop.openingHours.holidays = this.shop.openingHours.holidays.filter((h) => {
        if (!h.openingHours || h.openingHours.length === 0) {
          return true;
        }
        return TimeInterval.isValid(h.openingHours[0]);
      });
    }

    if (this.shop.storageRoomAccessCode) {
      // Remove whitespace from storage room access code
      this.shop.storageRoomAccessCode = this.shop.storageRoomAccessCode.replace(/\s/g, '');
    }

    // Get maps URL and add to the shop
    this.shop.mapsShortUrl = this.googleMapsService.getPlaceLink(this.shop);

    // Add or update the shop
    const saveShopTask = this.shop._id
      ? this.storageManager.updateStorageLocation(this.shop)
      : this.storageManager.addStorageLocation(this.shop);

    try {
      // Wait for the shop to be saved
      this.shop = await saveShopTask;

      await this.loginService.getUserInfo();

      if (doneEditing) {
        this.finish();
      }
    } catch (err) {
      const message = this.translate.instant('ERROR_SAVING_SHOP');
      const detail = err.error ? `: ${err.error.message}` : '';
      this.error.handleError(err, `${message}${detail}`);
    }

    this.isLoading = false;
  }

  notifyUserOfMissingInfo() {
    const title = this.translate.instant('MISSING_INFORMATION');
    let message: string;

    if (!this.form || !this.form.valid) {
      if (this.form) {
        const invalidFieldNames: string[] = [];

        for (const controlName in this.form.controls) {
          const control = this.form.controls[controlName];
          if (!control.valid && !control.disabled) {
            // Show validation errors on the UI
            control.markAsTouched();

            invalidFieldNames.push(this.getFriendlyControlName(controlName));
          }
        }

        if (invalidFieldNames.length > 0) {
          const invalidFieldsText = this.translate.instant('FOLLOWING_SHOP_FIELDS_NEEDED_BEFORE_SAVE');
          message = `${invalidFieldsText} ${invalidFieldNames.join(', ')}.`;
        }
      }
    } else if (this.isOnGoogle && !this.isPlaceSelected) {
      message = this.translate.instant('SHOP_GOOGLE_PLACE_NEEDED_BEFORE_SAVE');
    } else if (!this.openingHoursForm || !this.openingHoursForm.valid) {
      message = this.translate.instant('SHOP_OPENING_HOURS_NEEDED_BEFORE_SAVE');
    } else if (this.exceptionalOpeningHoursForm && !this.exceptionalOpeningHoursForm.valid) {
      message = this.translate.instant('EXCEPTIONAL_SHOP_HOURS_NEEDED_BEFORE_SAVE');
    } else if (this.requireHolidays && this.holidayOpeningHoursForm && !this.holidayOpeningHoursForm.valid) {
      message = this.translate.instant('SHOP_HOLIDAY_HOURS_NEEDED_BEFORE_SAVE');
    } else {
      message = this.translate.instant('NEED_ALL_SHOP_INFO');
    }

    this.notify.chat(message, title);
  }

  getFriendlyControlName(controlName: string): string {
    let translateKey: string;

    switch (controlName) {
      case 'name':
        translateKey = 'SHOP_NAME';
        break;

      case 'normal':
        translateKey = 'CAPACITY';
        break;

      case 'maxCapacityForAdvanceBookings':
        translateKey = 'MAX_CAPACITY_FOR_ADVANCE_BOOKINGS';
        break;

      case 'vatNumber':
        translateKey = 'VAT_NUMBER_PLACEHOLDER';
        break;

      case 'primaryContact':
        translateKey = 'NOTIFICATIONS_CONTACT';
        break;

      case 'street':
        translateKey = 'STREET';
        break;

      case 'city':
        translateKey = 'CITY';
        break;

      case 'postal':
        translateKey = 'POSTAL_CODE';
        break;

      case 'country':
        translateKey = 'COUNTRY';
        break;

      default:
        translateKey = controlName;
        break;
    }

    let friendlyName: string = this.translate.instant(translateKey);

    // Remove '(e.g. ...)' from placeholder texts
    friendlyName = friendlyName.split(' (')[0];

    return friendlyName;
  }

  async deleteShop() {
    this.isLoading = true;
    const confirmed = confirm(this.translate.instant('DELETE_SHOP_CONFIRMATION'));

    if (confirmed) {
      try {
        await this.storageManager.deleteStorageLocation(this.shop._id);
        this.finish();
      } catch (err) {
        // TODO: Handle errors
      }
    }
  }

  async refresh() {
    this.errorMessage = null;
    this.isLoading = true;

    if (this.shop._id) {
      this.shop = await this.storageManager.getStorageLocation(this.shop._id, true);
    }

    this.isLoading = false;
  }

  finish() {
    this.isLoading = false;

    if (this.isModal) {
      this.done.emit();
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  singleLineAddress(): string {
    const street = this.formatVal(this.shop.address.street);
    const zip = this.formatVal(this.shop.address.postal);
    const city = this.formatVal(this.shop.address.city);
    const country = this.formatVal(this.shop.address.country, false);
    return `${street} ${zip} ${city} ${country}`;
  }

  isMissing(control: FormControl): boolean {
    return control && control.enabled && control.invalid;
  }

  isComplete(control: FormControl): boolean {
    return control && control.enabled && control.valid;
  }

  printDebugInfo() {
    console.log(`this.canSaveShop`, this.canSaveShop);
    console.log(`this.shop`, this.shop);
    console.log(`this.form`, this.form);
    console.log(`this.openingHoursForm`, this.openingHoursForm);
  }

  prepareSection(section: number) {
    switch (section) {
      case 8:
        //
        // We have the minimum information required to save a new storage location after section 7; additional data
        // such as images will optionally be added in subsequent sections.
        //
        if (this.shop && !this.shop._id) {
          this.saveShop(false);
        }
        break;

      case 9:
        // Initialize payout account flow to allow setting up bank account
        this.initPayoutAccountFlow();
        break;
    }
  }

  async goForward() {
    if (!this.isLastSection) {
      this.goToSection(this.currentSection + 1);
      return;
    }
    // We are at the last section
    const proceed = await this.confirmSave();
    if (proceed) {
      this.saveShop(true);
      this.done.emit();
    }
  }

  goBack() {
    if (this.currentSection <= 1) {
      return;
    }
    this.goToSection(this.currentSection - 1);
  }

  goToSection(section: number) {
    if (this.isExistingShop && !this.isModal) {
      this.navigateToSection(section);
    } else {
      this.loadSection(section);
    }
  }

  navigateToSection(section: number) {
    if (!this.isSectionReachable(section)) {
      return;
    }
    this.router.navigate(['storage-locations', this.shop._id, 'sections', section]);
  }

  loadSection(section: number) {
    if (!this.isSectionReachable(section)) {
      return;
    }
    this.prepareSection(section);
    this.currentSection = section;
    this.requestedSection = 0;
    this.cd.markForCheck();

    this.windowService.scrollToTop();
  }

  goToFirstSectionWithMissingInfo() {
    let section = 0;

    while (section++ < this.numberOfSections) {
      if (!this.isSectionCompleted(section)) {
        this.goToSection(section);
        break;
      }
    }
  }

  async initPayoutAccountFlow(): Promise<void> {
    if (this.isPayoutAccountFlowInitialized) {
      return;
    }
    this.isLoading = true;
    try {
      this.payoutMethods = (await this.payoutService.getPayoutMethods()) || [];
      this.isPayoutAccountFlowInitialized = true;
    } catch (err) {
      this.error.handleError(err, this.translate.instant('ERROR_LOADING_PAYOUT_METHODS'));
    }
    this.isLoading = false;
    this.cd.detectChanges();
  }

  shouldSuggest(value: string, shopPropertyName: string): boolean {
    if (!this.shop || !value) {
      return false;
    }
    const existingValue = String(this.shop[shopPropertyName]);
    if (existingValue && existingValue.includes(value)) {
      return false;
    }
    return true;
  }

  applySuggestion(value: string, propertyName: string, append = false) {
    if (!this.shop) {
      return;
    }
    if (append && this.shop[propertyName]) {
      const values = String(this.shop[propertyName]).split(',');
      values.push(value);
      value = values.join(',');
    }
    this.shop[propertyName] = value;

    // HACK: Mark name input field as dirty to ensure that Save button is enabled
    this.form.controls.name.markAsDirty();
  }

  isSectionCompleted(section: number): boolean {
    switch (section) {
      case 1:
        if (this.isOnGoogle && !this.isPlaceSelected) {
          return false;
        }
        return this.isPartialFormValid('street', 'city', 'postal', 'country', 'directions');

      case 2:
        return this.isPartialFormValid('name', 'vatNumber', 'normal');

      case 3:
        return this.isPartialFormValid('primaryContact', 'email', 'sms', 'billingEmail');

      case 4:
        return this.isPartialFormValid('phone', 'contactEmail', 'website');

      case 5:
        if (!this.isWifiCompleted) {
          return false;
        }
        return this.isPartialFormValid('wifiSsid', 'wifiPass', 'wifiUser');

      case 6:
        if (!this.openingHoursForm || !this.openingHoursForm.valid) {
          return false;
        }
        if (this.exceptionalOpeningHoursForm && !this.exceptionalOpeningHoursForm.valid) {
          return false;
        }
        return true;

      case 7:
        return this.holidayOpeningHoursForm && this.holidayOpeningHoursForm.valid;

      //
      // No required input after section 7 (opening hours) so fall through and return true for all
      // of the last sections.
      //
      case 8:
      case 9:
        return true;

      // Return false for non-existent sections
      default:
        return true;
    }
  }

  private parseSection(value: unknown): number {
    let section: number;

    if (typeof value === 'string') {
      try {
        section = Number(value);
      } catch {
        // Ignore
      }
    }

    if (section && !isNaN(section) && section > 0 && section <= this.numberOfSections) {
      return section;
    }

    // Default to first section
    return 1;
  }

  private async confirmSave(): Promise<boolean> {
    if (this.payoutMethods.length > 0 || AppConfig.IS_STORAGE_MANAGER_SAVE_WITHOUT_PAYOUT_ENABLED) {
      return true;
    }
    try {
      const message = this.translate.instant('CONFIRM_SAVE_SHOP_WITHOUT_PAYOUT_METHOD');
      this.windowService.confirm(message);
    } catch {
      return false; // User cancelled
    }
    // Allow save to proceed
    return true;
  }

  private isSectionReachable(section: number): boolean {
    if (!section) {
      return false;
    }
    if (this.isExistingShop) {
      return true;
    }
    let currentSection = 0;
    while (++currentSection < section) {
      if (!this.isSectionCompleted(currentSection)) {
        if (!Config.isProduction) {
          console.log(`Section ${section} is not reachable because section ${currentSection} is not completed`);
        }
        return false;
      }
    }
    return true;
  }

  private isPartialFormValid(...formControlNames: string[]): boolean {
    if (!this.form) {
      return false;
    }
    for (let i = 0; i < formControlNames.length; i++) {
      const controlName = formControlNames[i];
      const control = this.form.controls[controlName];
      if (!control) {
        return false;
      }
      if (control.enabled && !control.valid) {
        return false;
      }
    }
    return true;
  }

  private areImagesEqual(a: string[], b: string[]): boolean {
    if (!a && !b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (!b.includes(a[i])) {
        return false;
      }
    }
    return true;
  }

  private async init() {
    this.isLoading = true;

    if (this.shopId) {
      let shop: ManageableStorageLocation;

      try {
        shop = await this.storageManager.getStorageLocation(this.shopId, false);
      } catch (err) {
        if ([401, 403].includes(err['status'])) {
          //
          // Not authorized to view this storage location; log out and navigate to the url again to trigger login
          //
          this.loginService.logout();
          await this.router.navigateByUrl(this.urlSnapshot);

          return;
        }

        throw err;
      }

      shop._id = this.shopId; // Ensure that shop has id set

      this.shop = shop;
      this.hasNoVatNumber = !shop.vatNumber;

      if (!shop.place || shop.place.id === null) {
        this.isNotOnGoogle = true;
      }

      // Initialize the map with the shop's current info
      this.mapCenter = shop.location?.coordinates
        ? { lat: shop.location.coordinates[1], lng: shop.location.coordinates[0] }
        : { lat: 0, lng: 0 }; // This should never happen
      this._mapShops = [this.shop];
    } else {
      this.shop = CreateManageableStorageLocation();

      // Start the map in the user's approximate location
      const { latitude, longitude } = await this.ipLookupService.locate();
      this.mapCenter = { lat: latitude, lng: longitude };
    }
    this.setShopDescription();

    this.isInitialized = true;
    this.isLoading = false;
    this.onShopReady();
  }

  private updateShopAddress(value: Address, autoFillForm = false) {
    if (!value) {
      return;
    }
    // Update the parts of the model that are not visible to the user
    this.shop.location.coordinates = value.location;
    this.shop.address.formattedAddress = value.formattedAddress;
    this.shop.address.countryCode = value.countryCode;
    // We don't use or allow this on the server side model
    // this.shop.address.region = value.region;
    this.shop.region = value.region;

    if (autoFillForm) {
      // Auto-fill address form fields
      this.shop.address.street = value.street;
      this.shop.address.city = value.city;
      this.shop.address.postal = value.postal;
      this.shop.address.country = value.country;
    }

    this.mapCenter = { lat: value.location[1], lng: value.location[0] };
    this._mapShops = [this.shop];
    this.cd.markForCheck();
  }

  private shouldBeUrlPrefixed(url: string): boolean {
    if (!url || url === '') {
      return false;
    }
    return !url.startsWith('http://') && !url.startsWith('https://');
  }

  private formatVal(val, addCommaEnd = true): string {
    return `${val ? val + (addCommaEnd ? ', ' : '') : ''}`;
  }
}
